import type { ApiSuccessResponse, PaginatedResponse } from "../types";

export type AuthTransport = "cookie" | "bearer" | "none";

type HeaderValue = string | number | boolean;
type HeaderTuple = [string, HeaderValue];
export type HeadersInput = Record<string, HeaderValue> | HeaderTuple[];

export interface RequestOptions {
  method?: string;
  headers?: HeadersInput;
  body?: string;
  credentials?: "include" | "omit";
}

interface FetchResponseLike {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}

type FetchLike = (
  url: string,
  options?: RequestOptions,
) => Promise<FetchResponseLike>;

export interface ApiClientOptions {
  baseUrl: string;
  transport: AuthTransport;
  getAccessToken?: () => string | null | Promise<string | null>;
  defaultHeaders?: HeadersInput;
}

async function toErrorMessage(response: FetchResponseLike): Promise<string> {
  const body = await response.json().catch(() => null);
  let message = `Request failed: ${response.status}`;

  if (body && typeof body === "object" && "error" in body) {
    const errorField = (body as { error: string | { message?: string } }).error;
    if (typeof errorField === "string") {
      message = errorField;
    } else if (
      errorField &&
      typeof errorField === "object" &&
      "message" in errorField
    ) {
      message = errorField.message ?? message;
    }
  }

  return message;
}

function joinUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function normalizeHeaders(headers?: HeadersInput): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(
      headers.map(([key, value]) => [key, String(value)]),
    );
  }

  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    normalized[key] = String(value);
  }
  return normalized;
}

export function createApiClient(options: ApiClientOptions) {
  const fetchFn = (globalThis as { fetch?: FetchLike }).fetch;

  async function request<T>(path: string, init?: RequestOptions): Promise<T> {
    if (!fetchFn) {
      throw new Error("Fetch API is not available in this runtime");
    }

    const headers: Record<string, string> = {
      ...normalizeHeaders(options.defaultHeaders),
      "Content-Type": "application/json",
      ...normalizeHeaders(init?.headers),
    };

    if (options.transport === "bearer" && options.getAccessToken) {
      const token = await options.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetchFn(joinUrl(options.baseUrl, path), {
      ...init,
      headers,
      credentials: options.transport === "cookie" ? "include" : "omit",
    });

    if (!response.ok) {
      throw new Error(await toErrorMessage(response));
    }

    return response.json() as Promise<T>;
  }

  return {
    get<T>(path: string) {
      return request<ApiSuccessResponse<T>>(path);
    },

    getPaginated<T>(path: string) {
      return request<PaginatedResponse<T>>(path);
    },

    post<T>(path: string, body?: unknown) {
      return request<ApiSuccessResponse<T>>(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
    },

    patch<T>(path: string, body?: unknown) {
      return request<ApiSuccessResponse<T>>(path, {
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
      });
    },

    put<T>(path: string, body?: unknown) {
      return request<ApiSuccessResponse<T>>(path, {
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
      });
    },

    delete<T>(path: string) {
      return request<ApiSuccessResponse<T>>(path, {
        method: "DELETE",
      });
    },
  };
}
